import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import {
  acceptAllConsent,
  refuseConsent,
  writeConsent,
  readConsent,
  CONSENT_EVENT,
  OPEN_BANNER_EVENT,
} from "../consent";
import { useDialogA11y, useFocusReturn } from "../a11y";

/* Banner cookie conforme alle Linee guida del Garante (10 giugno 2021):
   Accetta tutti / Rifiuta (stessa evidenza) / Personalizza; X = rifiuto;
   scorrimento non vincolante; nessuna casella preselezionata; rifiuto non
   riproposto per 6 mesi. Render solo lato client: mai nell'HTML prerenderizzato. */
export default function CookieBanner() {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [custom, setCustom] = useState(false);
  const [stat, setStat] = useState(false);
  const [terze, setTerze] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setMounted(true);
    /* navigator.webdriver = prerendering/automazione: il banner resta fuori
       dall'HTML statico ed è solo un'esperienza per gli utenti reali. */
    if (!readConsent() && !navigator.webdriver) setVisible(true);
    const open = () => {
      const c = readConsent();
      setStat(!!(c && c.statistiche));
      setTerze(!!(c && c.terze));
      setCustom(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_BANNER_EVENT, open);
    return () => window.removeEventListener(OPEN_BANNER_EVENT, open);
  }, []);

  const onCloseEsc = () => refuseConsent();
  useDialogA11y(ref, visible, onCloseEsc);
  useFocusReturn(visible);

  /* Il focus entra nel banner appena appare: senza questo il trap e Esc non intercettano nulla. */
  useEffect(() => {
    if (visible && ref.current) ref.current.querySelector(".cbox").focus();
  }, [visible, custom]);

  /* Ogni scelta registrata (anche da /cookie) chiude il banner. */
  useEffect(() => {
    const hide = () => setVisible(false);
    window.addEventListener(CONSENT_EVENT, hide);
    return () => window.removeEventListener(CONSENT_EVENT, hide);
  }, []);

  if (!mounted || !visible) return null;

  return (
    <div className="cwrap" ref={ref} role="dialog" aria-modal="true" aria-label="Informativa breve sui cookie" data-testid="cookie-banner">
      <div className="cbox" tabIndex={-1}>
        <button
          type="button"
          className="cclose"
          aria-label="Chiudi e rifiuta i cookie non necessari"
          onClick={() => refuseConsent()}
          data-testid="cookie-close"
        >
          <X size={18} />
        </button>
        <p className="ctitle">Informativa breve sui cookie</p>
        <p className="ctext">
          Usiamo archiviazione tecnica per far funzionare il sito: sicurezza della connessione,
          preferenze e funzionamento della chat dell'assistente. Con il tuo consenso attiveremo
          anche strumenti di statistica e servizi di terze parti. Lo scorrimento della pagina non
          equivale a consenso.{" "}
          <Link to="/cookie" data-testid="cookie-policy-link">
            Cookie policy
          </Link>
        </p>
        {!custom ? (
          <div className="cbtns">
            <button type="button" className="cbtn accept" onClick={() => acceptAllConsent()} data-testid="cookie-accept">
              Accetta tutti
            </button>
            <button type="button" className="cbtn refuse" onClick={() => refuseConsent()} data-testid="cookie-refuse">
              Rifiuta
            </button>
            <button type="button" className="cbtn link" onClick={() => setCustom(true)} data-testid="cookie-customize">
              Personalizza
            </button>
          </div>
        ) : (
          <div className="ccustom" data-testid="cookie-custom">
            <label className="ccat">
              <input type="checkbox" checked disabled data-testid="cookie-necessari" /> Necessari (sempre attivi)
            </label>
            <label className="ccat">
              <input type="checkbox" checked={stat} onChange={(e) => setStat(e.target.checked)} data-testid="cookie-stat" /> Statistiche
            </label>
            <label className="ccat">
              <input type="checkbox" checked={terze} onChange={(e) => setTerze(e.target.checked)} data-testid="cookie-terze" /> Servizi di terze parti
            </label>
            <div className="cbtns">
              <button type="button" className="cbtn accept" onClick={() => writeConsent(stat, terze)} data-testid="cookie-save">
                Salva preferenze
              </button>
              <button type="button" className="cbtn refuse" onClick={() => refuseConsent()} data-testid="cookie-refuse-2">
                Rifiuta
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
